// Versão: 1.02
// Data..: 2026-10-01
// v1.02: tela de "Link expirado" quando view.expired é true (bloqueia o formulário de assinatura).
// v1.01: opção de recusar a assinatura (com motivo) e tela de recusa.
// Página pública de assinatura eletrônica de contrato, acessada por token (sem login).
// O signatário revisa o documento (PDF), assina desenhando no quadro ou digitando o
// nome, aceita os termos e confirma. A assinatura e a trilha de auditoria são
// registradas no backend, que sela o PDF final quando todos assinam.

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicShell } from './PublicShell.js';
import { publicApi, type PublicSignView } from '../../lib/publicApi.js';
import { config } from '../../config.js';

export function PublicSignPage() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<PublicSignView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const [fullName, setFullName] = useState('');
  const [typedName, setTypedName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    if (!token) return;
    publicApi.getSignRequest(token)
      .then((v) => { setView(v); setFullName(v.signerName); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Link inválido'))
      .finally(() => setLoading(false));
  }, [token]);

  // --- Canvas de assinatura ---
  const getCtx = () => canvasRef.current?.getContext('2d') ?? null;
  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const startDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    hasDrawn.current = true;
  };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    const ctx = getCtx();
    if (ctx && canvasRef.current) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    hasDrawn.current = false;
  };

  const submit = async () => {
    if (!token) return;
    if (!fullName.trim()) { setError('Informe seu nome completo.'); return; }
    if (!accepted) { setError('É necessário aceitar os termos para assinar.'); return; }

    let signatureImage: string | undefined;
    let signatureType: 'drawn' | 'typed' = 'typed';
    if (mode === 'draw') {
      if (!hasDrawn.current) { setError('Desenhe sua assinatura ou use a opção de digitar o nome.'); return; }
      signatureImage = canvasRef.current?.toDataURL('image/png');
      signatureType = 'drawn';
    } else {
      if (!typedName.trim()) { setError('Digite seu nome para assinar.'); return; }
      signatureType = 'typed';
    }

    setError(null);
    setSending(true);
    try {
      await publicApi.submitSignature(token, { fullName: fullName.trim(), signatureImage, signatureType, accepted });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao assinar');
    } finally {
      setSending(false);
    }
  };

  // Recusa da assinatura (com motivo).
  const [declining, setDeclining] = useState(false); // painel de recusa aberto
  const [declineReason, setDeclineReason] = useState('');
  const [declinedDone, setDeclinedDone] = useState(false);
  const submitDecline = async () => {
    if (!token) return;
    setSending(true);
    try {
      await publicApi.declineSignature(token, declineReason.trim() || undefined);
      setDeclinedDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao recusar');
    } finally {
      setSending(false);
    }
  };

  const alreadySigned = view?.alreadySigned || done;
  const alreadyDeclined = view?.alreadyDeclined || declinedDone;

  return (
    <PublicShell>
      <div style={{ maxWidth: 640, margin: '48px auto 64px', padding: '0 24px' }}>
        {loading ? (
          <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>
        ) : error && !view ? (
          <div className="card empty"><div className="empty-icon">🔍</div>{error}</div>
        ) : !view ? (
          <div className="card empty"><div className="empty-icon">🔍</div>Contrato não encontrado ou link inválido.</div>
        ) : alreadyDeclined ? (
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44 }}>🚫</div>
            <h2>Assinatura recusada</h2>
            <p className="text-muted">Você recusou a assinatura do contrato "{view.title}". A Makweb foi notificada.</p>
          </div>
        ) : alreadySigned ? (
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44 }}>✅</div>
            <h2>Assinatura registrada!</h2>
            <p className="text-muted">Obrigado. Sua assinatura do contrato "{view.title}" foi registrada com sucesso.</p>
            <p className="text-dim text-sm">{view.signedCount + (done ? 1 : 0)} de {view.totalCount} assinaturas concluídas.</p>
            {view.documentUrl && <a className="btn btn-secondary mt-3" href={`${config.apiUrl}${view.documentUrl}`} target="_blank" rel="noreferrer">📄 Ver documento</a>}
          </div>
        ) : view.expired ? (
          // Link de assinatura expirado: não exibe o formulário de assinatura.
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44 }}>⏰</div>
            <h2>Link expirado</h2>
            <p className="text-muted">O prazo para assinar o contrato "{view.title}" venceu, então este link não está mais ativo.</p>
            <p className="text-dim text-sm">Se ainda deseja assinar, solicite um novo convite à Makweb que enviaremos um link atualizado.</p>
          </div>
        ) : (
          <div className="card fade-in">
            <div className="text-dim text-sm">Assinatura eletrônica{view.quoteNumber ? ` · Contrato nº ${view.quoteNumber}` : ''}</div>
            <h1 style={{ marginBottom: 4 }}>{view.title}</h1>
            <p className="text-muted">Olá, {view.signerName}{view.signerRole ? ` (${view.signerRole})` : ''}. Revise o contrato e assine abaixo.</p>

            {view.documentUrl && (
              <a className="btn btn-secondary w-full mt-2" href={`${config.apiUrl}${view.documentUrl}`} target="_blank" rel="noreferrer">📄 Ler o contrato (PDF)</a>
            )}

            <div className="field mt-4">
              <label className="label">Nome completo</label>
              <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>

            {/* Modo de assinatura */}
            <div className="flex gap-2 mt-2" role="tablist">
              <button type="button" className={`btn btn-sm ${mode === 'draw' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('draw')}>✍️ Desenhar</button>
              <button type="button" className={`btn btn-sm ${mode === 'type' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('type')}>⌨️ Digitar nome</button>
            </div>

            {mode === 'draw' ? (
              <div className="mt-2">
                <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 10, background: '#fff', touchAction: 'none' }}>
                  <canvas
                    ref={canvasRef}
                    width={560}
                    height={180}
                    style={{ width: '100%', height: 180, cursor: 'crosshair', display: 'block' }}
                    onPointerDown={startDraw}
                    onPointerMove={moveDraw}
                    onPointerUp={endDraw}
                    onPointerLeave={endDraw}
                  />
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-dim text-xs">Assine no quadro acima usando o mouse ou o dedo.</span>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={clearCanvas}>Limpar</button>
                </div>
              </div>
            ) : (
              <div className="field mt-2">
                <label className="label">Digite seu nome como assinatura</label>
                <input className="input" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Seu nome" style={{ fontFamily: 'cursive', fontSize: 22 }} />
              </div>
            )}

            <label className="flex items-center gap-2 mt-4" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span className="text-sm">Li e concordo com os termos do contrato e assino eletronicamente, conforme a MP 2.200-2/2001.</span>
            </label>

            {error && <div className="text-sm mt-2" style={{ color: 'var(--danger)' }}>{error}</div>}

            <button className="btn btn-primary btn-lg w-full mt-4" disabled={sending} onClick={submit}>
              {sending ? 'Registrando...' : 'Assinar contrato'}
            </button>
            <div className="text-dim text-xs text-center mt-2">
              Ao assinar, registramos data/hora, seu IP e dispositivo para fins de auditoria.
            </div>

            {/* Recusar assinatura */}
            {!declining ? (
              <button className="btn btn-ghost w-full mt-2" type="button" onClick={() => { setError(null); setDeclining(true); }}>
                Recusar assinatura
              </button>
            ) : (
              <div className="mt-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--s3)' }}>
                <label className="label">Motivo da recusa (opcional)</label>
                <textarea className="textarea" rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Descreva o motivo, se desejar" />
                <div className="flex gap-2 mt-2">
                  <button className="btn btn-ghost" type="button" onClick={() => setDeclining(false)}>Cancelar</button>
                  <button className="btn btn-danger" type="button" disabled={sending} onClick={submitDecline}>
                    {sending ? 'Enviando...' : 'Confirmar recusa'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
